import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { createLogger } from '../utils/logger'

export default class extends Controller {
  static values = {
    buildSessionId: String,
    channelUrl: String
  }

  connect() {
    this.logger = createLogger('devtools-cable')
    this.setupCableConnection()
    this.setupHeartbeat()
  }

  disconnect() {
    this.teardownCableConnection()
    this.teardownHeartbeat()
  }

  setupCableConnection() {
    const cableUrl = this.channelUrlValue || this.getDefaultCableUrl()
    this.consumer = createConsumer(cableUrl)
    
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: "DevtoolsChannel",
        build_session_id: this.buildSessionIdValue
      },
      {
        connected: this.handleConnected.bind(this),
        disconnected: this.handleDisconnected.bind(this),
        received: this.handleReceived.bind(this),
        rejected: this.handleRejected.bind(this)
      }
    )
  }

  teardownCableConnection() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    
    if (this.consumer) {
      this.consumer.disconnect()
      this.consumer = null
    }
  }

  getDefaultCableUrl() {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    return `${protocol}//${host}/cable`
  }

  handleConnected() {
    this.logger.debug('DevTools channel connected')
    this.dispatch('cable-connected', { detail: { buildSessionId: this.buildSessionIdValue } })
    this.updateConnectionStatus('connected')
  }

  handleDisconnected() {
    this.logger.debug('DevTools channel disconnected')
    this.dispatch('cable-disconnected', { detail: { buildSessionId: this.buildSessionIdValue } })
    this.updateConnectionStatus('disconnected')
  }

  handleReceived(data) {
    // Forward received data to appropriate devtools panels
    switch (data.type) {
      case 'console':
        this.forwardToConsole(data.data)
        break
      case 'network':
        this.forwardToNetwork(data.data)
        break
      case 'error':
        this.forwardToErrors(data.data)
        break
      case 'performance':
        this.forwardToPerformance(data.data)
        break
      case 'dom':
        this.forwardToDOM(data.data)
        break
      case 'pong':
        this.handlePong(data)
        break
      case 'gem-monitoring':
        // Handle monitoring data from AllSpark gem webhooks
        this.handleGemMonitoringData(data)
        break
      case 'claude_command_completed':
        // Handle Claude Code command completion
        this.handleClaudeCommandCompleted(data)
        break
      default:
        this.logger.debug('Unknown devtools message type:', data.type)
    }
  }

  handleRejected() {
    this.logger.error('DevTools channel subscription rejected')
    this.dispatch('cable-rejected', { detail: { buildSessionId: this.buildSessionIdValue } })
    this.updateConnectionStatus('rejected')
  }

  forwardToConsole(data) {
    const consoleEvent = new CustomEvent('devtools:console-update', {
      detail: data,
      bubbles: true
    })
    this.element.dispatchEvent(consoleEvent)
  }

  forwardToNetwork(data) {
    const networkEvent = new CustomEvent('devtools:network-update', {
      detail: data,
      bubbles: true
    })
    this.element.dispatchEvent(networkEvent)
  }

  forwardToErrors(data) {
    const errorEvent = new CustomEvent('devtools:error-update', {
      detail: data,
      bubbles: true
    })
    this.element.dispatchEvent(errorEvent)
  }

  forwardToPerformance(data) {
    const performanceEvent = new CustomEvent('devtools:performance-update', {
      detail: data,
      bubbles: true
    })
    this.element.dispatchEvent(performanceEvent)
  }

  forwardToDOM(data) {
    const domEvent = new CustomEvent('devtools:dom-update', {
      detail: data,
      bubbles: true
    })
    this.element.dispatchEvent(domEvent)
  }

  // Methods to send data to the channel
  sendConsoleLog(logData) {
    this.send('console_log', logData)
  }

  sendNetworkRequest(requestData) {
    this.send('network_request', requestData)
  }

  sendErrorReport(errorData) {
    this.send('error_report', errorData)
  }

  sendPerformanceUpdate(performanceData) {
    this.send('performance_update', performanceData)
  }

  sendDOMUpdate(domData) {
    this.send('dom_update', domData)
  }

  send(action, payload) {
    if (this.subscription) {
      this.subscription.send({
        action: action,
        payload: payload
      })
    } else {
      console.warn('Cannot send devtools data: no active subscription')
    }
  }

  // Heartbeat functionality
  setupHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat()
    }, 30000) // Every 30 seconds
  }

  teardownHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  sendHeartbeat() {
    if (this.subscription) {
      this.lastPingTime = Date.now()
      this.send('ping', { timestamp: new Date().toISOString() })
    }
  }

  handlePong(data) {
    if (this.lastPingTime) {
      const latency = Date.now() - this.lastPingTime
      this.updateConnectionLatency(latency)
    }
  }

  updateConnectionStatus(status) {
    const statusElement = this.element.querySelector('[data-connection-status]')
    if (statusElement) {
      statusElement.textContent = status
      statusElement.className = `connection-status connection-status--${status}`
    }

    // Update status indicator
    const indicator = this.element.querySelector('[data-connection-indicator]')
    if (indicator) {
      indicator.className = `connection-indicator connection-indicator--${status}`
    }
  }

  updateConnectionLatency(latency) {
    const latencyElement = this.element.querySelector('[data-connection-latency]')
    if (latencyElement) {
      latencyElement.textContent = `${latency}ms`
    }
  }
  
  handleClaudeCommandCompleted(data) {
    this.logger.info('Claude command completed notification received', data)
    
    // Dispatch a global event that the story execution controller can listen to
    const event = new CustomEvent('claude:command-completed', {
      detail: {
        command_id: data.command_id,
        session_id: data.session_id,
        build_session_id: data.build_session_id,
        timestamp: data.timestamp
      },
      bubbles: true
    })
    
    document.dispatchEvent(event)
    
    // Also dispatch a local event in case components are listening to this controller
    this.dispatch('claude-command-completed', { detail: data })
  }

  // Public API for other controllers to use
  isConnected() {
    return this.subscription && this.subscription.consumer.connection.isOpen()
  }

  reconnect() {
    this.teardownCableConnection()
    setTimeout(() => {
      this.setupCableConnection()
    }, 1000)
  }

  // Export functionality
  exportSessionData() {
    const data = {
      buildSessionId: this.buildSessionIdValue,
      exportedAt: new Date().toISOString(),
      connectionStatus: this.isConnected() ? 'connected' : 'disconnected'
    }
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `devtools-session-${this.buildSessionIdValue}-${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // State synchronization
  syncState(state) {
    this.send('sync_state', {
      state: state,
      timestamp: new Date().toISOString()
    })
  }

  requestStateSync() {
    this.send('request_state_sync', {
      timestamp: new Date().toISOString()
    })
  }

  handleGemMonitoringData(data) {
    // Process monitoring data from AllSpark gem webhooks
    const { event_type, data: eventData } = data
    
    switch (event_type) {
      case 'rails_error':
        this.forwardToErrors({
          message: eventData.message,
          severity: eventData.level || 'error',
          stack: eventData.backtrace?.join('\n'),
          timestamp: eventData.timestamp,
          context: eventData.context
        })
        break
        
      case 'console_log':
        this.forwardToConsole({
          level: eventData.level || 'info',
          message: eventData.message,
          timestamp: eventData.timestamp,
          source: 'allspark-gem'
        })
        break
        
      case 'network_request':
        this.forwardToNetwork({
          url: eventData.url,
          method: eventData.method,
          status: eventData.status,
          duration: eventData.duration,
          timestamp: eventData.timestamp,
          headers: eventData.headers,
          response_size: eventData.response_size
        })
        break
        
      case 'performance_metrics':
        this.forwardToPerformance({
          memory_used: eventData.memory_usage,
          cpu_usage: eventData.cpu_usage,
          active_record_queries: eventData.active_record_queries,
          cache_hits: eventData.cache_hits,
          cache_misses: eventData.cache_misses,
          timestamp: eventData.timestamp
        })
        break
        
      case 'database_query':
        // Forward to console with special formatting for SQL
        this.forwardToConsole({
          level: 'sql',
          message: `[${eventData.duration}ms] ${eventData.sql}`,
          timestamp: eventData.timestamp,
          source: 'active_record',
          metadata: {
            name: eventData.name,
            binds: eventData.binds,
            cached: eventData.cached
          }
        })
        break
        
      case 'cache_operation':
        // Forward cache operations to performance panel
        this.forwardToPerformance({
          cache_operation: eventData.operation,
          cache_key: eventData.key,
          cache_hit: eventData.hit,
          timestamp: eventData.timestamp
        })
        break
        
      case 'view_render':
        // Forward view rendering info to performance panel
        this.forwardToPerformance({
          view_template: eventData.template,
          render_time: eventData.duration,
          timestamp: eventData.timestamp
        })
        break
        
      case 'action_controller':
        // Forward controller actions to network panel
        this.forwardToNetwork({
          controller: eventData.controller,
          action: eventData.action,
          method: eventData.method,
          path: eventData.path,
          status: eventData.status,
          duration: eventData.duration,
          timestamp: eventData.timestamp,
          params: eventData.params
        })
        break
        
      default:
        console.debug('Unknown gem monitoring event type:', event_type, eventData)
        // Forward unknown events to console for debugging
        this.forwardToConsole({
          level: 'debug',
          message: `[AllSpark Gem] ${event_type}: ${JSON.stringify(eventData)}`,
          timestamp: eventData.timestamp || new Date().toISOString(),
          source: 'allspark-gem-unknown'
        })
    }
  }
}